/**
 * Profildaten — Schalter der Sektionen 02–07.
 *
 * Die Sektionen Versicherung, Pflege, Charakter, Wunsch-BK, Freitexte und SIS
 * werden nur bei Bedarf aktiviert und ausgefüllt. Gespeichert wird der
 * Schalter je Profil in `profiles.sektionen_aktiv` (JSON-Objekt, Backend-
 * Migration 2026_09_28_100001).
 *
 * Fehlt der Schlüssel (Bestandsprofile, nie geschaltet), gilt eine Sektion als
 * aktiv, sobald eines ihrer Felder ausgefüllt ist — so verschwinden bereits
 * erfasste Daten nicht hinter einem ausgeschalteten Schalter.
 *
 * Eine inaktive Sektion wird nicht gespeichert (ihre Felder bleiben
 * unverändert in der DB) und ihre Pflichtfelder gelten nicht.
 */
import type { PbFormState } from './sections';

export const TOGGLE_SECTIONS = {
  versicherung: [
    'krankenkasse', 'krankenkasse_typ', 'kranken_vnr', 'sozial_vnr', 'renten_vnr',
    'steuer_id', 'lbnr', 'sozialamt_aufstockung',
  ],
  pflege: [
    'groesse', 'gewicht', 'raucher', 'pflegegrad', 'beweglichkeit', 'einschraenkungen',
    'sonstige_einschraenkungen', 'besserung', 'sinneswahrnehmung', 'geisteszustand',
    'schlafverhalten', 'grundpflege', 'hygiene', 'ankleiden', 'essen', 'urinkontrolle',
    'stuhlkontrolle', 'sgb5', 'sgb5_medigabe', 'sgb5_medigabe_taeglich', 'sgb5_ats',
  ],
  charakter: ['ausgepraegtes_familienleben', 'charaktereigenschaften'],
  wunsch: ['bk_anrede', 'bk_aktivitaeten', 'bk_deutsch', 'bk_fuehrerschein', 'bk_raucher'],
  freitexte: ['hobbys', 'umfeld', 'sonstiges'],
  sis: [
    'freitext_sis_1', 'freitext_sis_2', 'freitext_sis_3', 'freitext_sis_4', 'freitext_sis_5',
    'freitext_sis_6', 'freitext_sis_7', 'freitext_sis_8', 'freitext_sis_9',
  ],
} as const satisfies Record<string, ReadonlyArray<keyof PbFormState>>;

export type ToggleSectionId = keyof typeof TOGGLE_SECTIONS;

function hasValue(v: unknown): boolean {
  if (v == null || v === '' || v === false) return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** Ist die Sektion aktiv? Gespeicherter Schalter, sonst „hat Daten". */
export function isSectionActive(f: PbFormState, id: ToggleSectionId): boolean {
  const stored = f.sektionen_aktiv?.[id];
  if (typeof stored === 'boolean') return stored;
  return TOGGLE_SECTIONS[id].some(k => hasValue(f[k]));
}

/**
 * Ergänzt fehlende Schalter um den abgeleiteten Zustand („hat Daten").
 * Beim Laden aufrufen: sonst wird eine Sektion inaktiv, sobald man ihr
 * letztes Feld leert — und das Leeren landet dann nicht im Save-Payload.
 */
export function freezeSektionenAktiv(f: PbFormState): PbFormState {
  const frozen: Partial<Record<ToggleSectionId, boolean>> = { ...(f.sektionen_aktiv ?? {}) };
  for (const id of Object.keys(TOGGLE_SECTIONS) as ToggleSectionId[]) {
    if (typeof frozen[id] !== 'boolean') frozen[id] = isSectionActive(f, id);
  }
  return { ...f, sektionen_aktiv: frozen };
}

/** Felder aller inaktiven Sektionen — werden beim Speichern weggelassen. */
export function inactiveSectionFields(f: PbFormState): Set<string> {
  const out = new Set<string>();
  for (const id of Object.keys(TOGGLE_SECTIONS) as ToggleSectionId[]) {
    if (!isSectionActive(f, id)) TOGGLE_SECTIONS[id].forEach(k => out.add(k));
  }
  return out;
}

/** `sektionen_aktiv` aus der API (Objekt oder JSON-String) → Formular-State. */
export function parseSektionenAktiv(v: unknown): PbFormState['sektionen_aktiv'] {
  let obj = v;
  if (typeof v === 'string' && v.startsWith('{')) {
    try { obj = JSON.parse(v); } catch { return undefined; }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  const out: Partial<Record<ToggleSectionId, boolean>> = {};
  for (const id of Object.keys(TOGGLE_SECTIONS) as ToggleSectionId[]) {
    const val = (obj as Record<string, unknown>)[id];
    if (typeof val === 'boolean') out[id] = val;
  }
  return out;
}
